import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { BarChart3, ArrowLeft, AlertTriangle, CheckCircle, Clock, Users, Euro, Award } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { supplierNcService, supplierService } from '../services/api';

const CLASS_COLOR = { A: 'bg-emerald-600 text-white', B: 'bg-sky-500 text-white', C: 'bg-amber-500 text-white', D: 'bg-red-600 text-white' };
const CAT_COLORS = ['#0ea5e9', '#f59e0b', '#dc2626', '#8b5cf6', '#059669', '#f97316', '#3b82f6', '#ec4899', '#14b8a6', '#84cc16', '#64748b'];

const Kpi = ({ label, value, icon: Icon, color = 'slate' }) => {
  const bg = { slate:'bg-slate-50 border-slate-200 text-slate-800', red:'bg-red-50 border-red-200 text-red-800',
    emerald:'bg-emerald-50 border-emerald-200 text-emerald-800', amber:'bg-amber-50 border-amber-200 text-amber-800',
    sky:'bg-sky-50 border-sky-200 text-sky-800', violet:'bg-violet-50 border-violet-200 text-violet-800' }[color] || 'bg-slate-50';
  return (
    <div className={`border-2 rounded-lg p-4 ${bg}`}>
      <div className="flex items-center justify-between"><p className="text-xs uppercase tracking-wider font-bold opacity-70">{label}</p>{Icon && <Icon className="w-5 h-5 opacity-60"/>}</div>
      <p className="text-2xl font-bold mt-1 truncate">{value ?? '—'}</p>
    </div>
  );
};

export default function SupplierEvaluationDashboardPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [supplierId, setSupplierId] = useState('');
  const [severity, setSeverity] = useState('');
  const [defectCatId, setDefectCatId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  const [enums, setEnums] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const params = { year };
      if (supplierId) params.supplier_id = supplierId;
      if (severity) params.severity = severity;
      if (defectCatId) params.defect_category_id = defectCatId;
      const res = await supplierNcService.evaluationDashboard(params);
      setData(res.data);
    } catch { toast.error('Errore dashboard'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [year, supplierId, severity, defectCatId]);
  useEffect(() => {
    (async () => {
      try {
        const [s, e] = await Promise.all([supplierService.getAll(), supplierNcService.enums()]);
        setSuppliers(s.data || []); setEnums(e.data || {});
      } catch { /* silent */ }
    })();
  }, []);

  if (loading && !data) return <div className="text-center py-16"><div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto"/></div>;
  if (!data) return null;
  const k = data.kpis;

  return (
    <div className="space-y-6 pb-16" data-testid="supplier-eval-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Link to="/supplier-ncs"><Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-1"/>Elenco NC</Button></Link>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><BarChart3 className="w-6 h-6 text-orange-600"/>Dashboard Valutazione Fornitori</h1>
            <p className="text-sm text-slate-500 mt-1">Analisi annuale delle NC fornitori con score e classe di valutazione</p>
          </div>
        </div>
        <div className="flex items-end gap-2">
          <div><Label className="text-xs">Anno</Label><Input type="number" className="w-24" value={year} onChange={e=>setYear(Number(e.target.value)||now.getFullYear())} data-testid="filter-year"/></div>
          <div><Label className="text-xs">Fornitore</Label>
            <Select value={supplierId || 'all'} onValueChange={v=>setSupplierId(v==='all'?'':v)}>
              <SelectTrigger className="w-48"><SelectValue placeholder="Tutti"/></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti</SelectItem>{suppliers.map(s=><SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Gravità</Label>
            <Select value={severity || 'all'} onValueChange={v=>setSeverity(v==='all'?'':v)}>
              <SelectTrigger className="w-32"><SelectValue placeholder="Tutte"/></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutte</SelectItem>{(enums.severities||[]).map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Categoria</Label>
            <Select value={defectCatId || 'all'} onValueChange={v=>setDefectCatId(v==='all'?'':v)}>
              <SelectTrigger className="w-40"><SelectValue placeholder="Tutte"/></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutte</SelectItem>{(enums.defect_categories||[]).map(c=><SelectItem key={c.id} value={c.id}>{c.category_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Kpi label="NC Emesse" value={k.total} icon={AlertTriangle} color="slate"/>
        <Kpi label="Aperte" value={k.open} icon={Clock} color="amber"/>
        <Kpi label="Chiuse" value={k.closed} icon={CheckCircle} color="emerald"/>
        <Kpi label="Scadute" value={k.overdue} icon={AlertTriangle} color={k.overdue>0?'red':'slate'}/>
        <Kpi label="Q.tà coinvolta" value={k.total_quantity} icon={Users} color="sky"/>
        <Kpi label="Tempo medio risposta" value={k.avg_response_days!==null?`${k.avg_response_days} gg`:'—'} icon={Clock} color="sky"/>
        <Kpi label="% Risposte accettate" value={`${k.accepted_pct}%`} icon={CheckCircle} color="emerald"/>
        <Kpi label="€ Note di debito" value={`€ ${(k.total_debit||0).toFixed(2)}`} icon={Euro} color="violet"/>
        <Kpi label="Fornitore top NC" value={k.top_supplier} icon={Users} color="red"/>
        <Kpi label="Categoria più frequente" value={k.top_defect_category} icon={Award} color="amber"/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly trend */}
        <Card><CardHeader><CardTitle className="text-base">NC Fornitori per Mese</CardTitle></CardHeader><CardContent>
          {data.monthly_trend.length===0 ? <p className="text-center text-slate-400 py-8">Nessun dato</p> :
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.monthly_trend}><CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/><XAxis dataKey="month" tick={{fontSize:11}}/><YAxis allowDecimals={false} tick={{fontSize:11}}/><Tooltip/><Bar dataKey="count" fill="#f97316" radius={[4,4,0,0]}/></BarChart>
          </ResponsiveContainer>}
        </CardContent></Card>

        {/* Pie by defect category */}
        <Card><CardHeader><CardTitle className="text-base">NC per Categoria Difetto</CardTitle></CardHeader><CardContent>
          {data.by_defect_category.length===0 ? <p className="text-center text-slate-400 py-8">Nessun dato</p> :
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={data.by_defect_category} dataKey="count" nameKey="category" cx="50%" cy="50%" outerRadius={85} label={({category,count})=>`${category}: ${count}`} labelLine={false}>
                {data.by_defect_category.map((_, i)=><Cell key={i} fill={CAT_COLORS[i % CAT_COLORS.length]}/>)}
              </Pie>
              <Tooltip/><Legend verticalAlign="bottom" height={30} wrapperStyle={{fontSize:11}}/>
            </PieChart>
          </ResponsiveContainer>}
        </CardContent></Card>

        {/* By supplier count */}
        <Card><CardHeader><CardTitle className="text-base">NC per Fornitore</CardTitle></CardHeader><CardContent>
          {data.by_supplier_count.length===0 ? <p className="text-center text-slate-400 py-8">Nessun dato</p> :
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.by_supplier_count} layout="vertical" margin={{left:80,right:10}}><CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/><XAxis type="number" allowDecimals={false} tick={{fontSize:11}}/><YAxis type="category" dataKey="supplier" width={120} tick={{fontSize:10}}/><Tooltip/><Bar dataKey="count" fill="#3b82f6" radius={[0,4,4,0]}/></BarChart>
          </ResponsiveContainer>}
        </CardContent></Card>

        {/* Debit by supplier */}
        <Card><CardHeader><CardTitle className="text-base">Note di Debito per Fornitore (€)</CardTitle></CardHeader><CardContent>
          {data.by_supplier_debit.length===0 || data.by_supplier_debit.every(x=>x.amount===0) ? <p className="text-center text-slate-400 py-8">Nessuna nota di debito</p> :
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.by_supplier_debit} layout="vertical" margin={{left:80,right:10}}><CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/><XAxis type="number" tick={{fontSize:11}}/><YAxis type="category" dataKey="supplier" width={120} tick={{fontSize:10}}/><Tooltip formatter={(v)=>`€ ${v.toFixed(2)}`}/><Bar dataKey="amount" fill="#8b5cf6" radius={[0,4,4,0]}/></BarChart>
          </ResponsiveContainer>}
        </CardContent></Card>
      </div>

      {/* Top suppliers table */}
      <Card><CardHeader><CardTitle className="text-base flex items-center gap-2"><Award className="w-4 h-4"/>Top Fornitori per numero NC (score & classe {data.year})</CardTitle></CardHeader><CardContent>
        {data.top_suppliers.length===0 ? <p className="text-center text-slate-400 py-8">Nessun fornitore con NC nel {data.year}</p> :
        <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="top-suppliers-table">
          <thead><tr className="border-b bg-slate-50">{['#','Fornitore','NC','Aperte','Chiuse','Q.tà','€ Note debito','Tempo medio','Score','Classe'].map(h=><th key={h} className="px-3 py-2 text-left font-medium text-slate-600">{h}</th>)}</tr></thead>
          <tbody>
            {data.top_suppliers.map((s,i)=>(
              <tr key={s.supplier_id} className="border-b last:border-0 hover:bg-slate-50">
                <td className="px-3 py-2 font-mono text-xs text-slate-500">{i+1}</td>
                <td className="px-3 py-2 font-medium">{s.supplier_name}</td>
                <td className="px-3 py-2 font-bold text-red-600">{s.nc_count}</td>
                <td className="px-3 py-2">{s.open}</td>
                <td className="px-3 py-2 text-emerald-700">{s.closed}</td>
                <td className="px-3 py-2">{s.quantity_involved}</td>
                <td className="px-3 py-2 text-violet-700 font-mono">€ {s.debit_amount.toFixed(2)}</td>
                <td className="px-3 py-2 font-mono text-xs">{s.avg_response_days!==null?`${s.avg_response_days} gg`:'—'}</td>
                <td className="px-3 py-2 font-bold">{s.score}</td>
                <td className="px-3 py-2"><Badge className={`${CLASS_COLOR[s.class]||''} px-3 py-1 font-bold text-base`}>{s.class}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table></div>}
      </CardContent></Card>
    </div>
  );
}
